/**
 * ONE — the merged map. THE THIRD SITE.
 *
 * `/` is NavApp (street nav) and `/map` is App (the hole map). Two MapLibre
 * instances, two shells, and a golfer bounced between what look like two
 * different products. This is both of them on one URL and one map.
 *
 * 🟥 It is a CLONE ON PURPOSE, not a refactor of either. `/` is what
 * CarPlaySceneDelegate loads and its street turn-by-turn is what the CarPlay
 * entitlement (case 21536472) was granted against — Apple's navigation category
 * is literally "turn-by-turn route guidance". Breaking `/` while experimenting
 * risks the entitlement. So `/one` is a sandbox: break it freely, both originals
 * keep working, and nothing here reaches CarPlay until it is deliberately moved.
 *
 * The merge is two switches in Settings rather than two screens:
 *   courseMode  on  -> hole corridor, green, hazards, yardages, hole < >
 *               off -> a plain positioned map, which is what driving there wants
 *   mapMode     poly | satellite | hybrid | road
 * Crossed, those give golf-poly, golf-satellite and golf-hybrid off one map.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapScreen from './screens/MapScreen';
import ScoreScreen from './screens/ScoreScreen';
import FleetScreen from './screens/FleetScreen';
import SetupScreen from './screens/SetupScreen';
import SurveyScreen from './screens/SurveyScreen';
import PostScoreScreen from './screens/PostScoreScreen';
import CoursesScreen from './screens/CoursesScreen';
import AnalyticsPanel from './screens/AnalyticsPanel.tsx';
import SwipePager from './components/SwipePager.tsx';
import BottomNav from './components/BottomNav.tsx';
import CommandSheet from './components/CommandSheet.tsx';
import WidgetScreen from './screens/WidgetScreen';
import { BRAND } from './lib/brand';
import { FEATURES } from './lib/features.ts';
import { EMPTY_COURSE, provisionalCourse, type LoadFailure } from './lib/courses';
import { useParked } from './lib/parked.ts';
import { unlockVoice, voiceSupported } from './lib/voice.ts';
import { initialState, setManual, step as detectStep, type DetectorState } from './lib/holeDetector.ts';
import { loadCartPaths } from './lib/cartpaths.ts';
import { parseCommand } from './lib/commands.ts';
import { loadDirectory } from './lib/directory.ts';
import type { FeatureCollection } from 'geojson';
import type { SearchHit } from './lib/directory';
import { getRegion } from '../../shared/regions';
import { useSettings, useWakeLock } from './lib/settings';
import { publishCarSettings } from './lib/settingsBridge.ts';
import { useGps } from './lib/gps';
import { useFleet } from './lib/fleet';
import { loadCourse } from './lib/courses';
import { fetchWeather } from './lib/weather';
import { apiPostDurable, drainOutbox, outboxSize } from './lib/api';
import { newRound } from './lib/scoring';
import { destination, distanceM, firstBreachedZone } from './lib/geo';
import type { Course, HoleScore, LngLat, Player, Round, ScoreFormat, Weather } from './lib/types';

type Tab = 'map' | 'score' | 'post' | 'courses' | 'fleet' | 'survey' | 'setup' | 'widget';

const ROUND_KEY = 'fairway.round.v1';

export default function OneApp() {
  const { settings, update } = useSettings();
  useWakeLock(settings.keepAwake);

  // The car screen shares no storage with this app (different origins), so the
  // App Group carries the settings across. Published on every change rather
  // than on a timer: CarPlay may connect at any moment and must not be stale.
  useEffect(() => {
    void publishCarSettings({
      indicators: settings.indicators,
      mapMode: settings.mapMode,
      teeColour: settings.teeColour,
      unit: settings.unit,
      lockToHole: settings.lockToHole,
      courseId: settings.courseId,
    });
  }, [settings.indicators, settings.mapMode, settings.teeColour, settings.unit,
      settings.lockToHole, settings.courseId]);

  // One theme attribute for the whole shell. styles.css keys every token off
  // :root[data-theme='light'], so this single line themes the map chrome, the
  // yardage cards, the hole banner and the tab bar together — nothing themes
  // itself locally, which is what keeps light mode from going half-applied.
  useEffect(() => {
    document.documentElement.dataset.theme = settings.theme;
  }, [settings.theme]);

  // Until a course is picked there is nothing to draw. The map still runs —
  // satellite imagery and your own position are true regardless — but no hole
  // is invented to fill the screen.
  const chosen = settings.courseId != null;
  const [course, setCourse] = useState<Course>(EMPTY_COURSE);
  const [courseLive, setCourseLive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<LoadFailure | undefined>();
  /** The directory knows the name before the holes arrive; use it while building. */
  const [pickedName, setPickedName] = useState(settings.courseName ?? '');
  const [sheetOpen, setSheetOpen] = useState(false);
  /** Bumped to force a course reload. Setting courseId to the value it already
   *  holds does not change the effect's dependency, so "Try again" did nothing. */
  const [reloadNonce, setReloadNonce] = useState(0);
  const courseName = course.name || pickedName || 'This course';
  /*
   * ?tab= deep-links a surface, which is what makes the buggy launcher
   * possible: a tile opens the tab it promises instead of dropping the golfer
   * on the map to go and find it. Unknown or disabled values fall through to
   * the normal default rather than showing an empty shell.
   */
  const [tab, setTab] = useState<Tab>(() => {
    const want = new URLSearchParams(location.search).get('tab') as Tab | null;
    const allowed: Tab[] = ['map', 'courses', 'setup', 'widget',
      ...(FEATURES.fleet ? (['fleet'] as Tab[]) : []),
      ...(FEATURES.survey ? (['survey'] as Tab[]) : []),
      ...(FEATURES.scoring ? (['score'] as Tab[]) : []),
      ...(FEATURES.federation ? (['post'] as Tab[]) : [])];
    if (want && allowed.includes(want)) return want;
    return settings.role === 'shop' ? 'fleet' : 'map';
  });
  const [detector, setDetector] = useState<DetectorState>(() => initialState(1));
  const holeIndex = Math.max(
    0,
    course.holes.findIndex((h) => h.number === detector.hole)
  );
  const [weather, setWeather] = useState<Weather | null>(null);
  const [round, setRound] = useState<Round | null>(() => {
    try {
      const raw = localStorage.getItem(ROUND_KEY);
      return raw ? (JSON.parse(raw) as Round) : null;
    } catch {
      return null;
    }
  });
  const [queued, setQueued] = useState(outboxSize());
  const [panel, setPanel] = useState(0);
  const [simPos, setSimPos] = useState<LngLat | null>(null);
  const [cartPaths, setCartPaths] = useState<FeatureCollection | null>(null);
  const { parked, park, clear: clearParked } = useParked();

  const hole = course.holes.length ? course.holes[Math.min(holeIndex, course.holes.length - 1)] : null;

  // --- course -------------------------------------------------------------
  useEffect(() => {
    const id = settings.courseId;
    if (id == null) {
      setCourse(EMPTY_COURSE);
      setCourseLive(false);
      setFailure(undefined);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setFailure(undefined);
    loadCourse(id).then(({ course: c, live, failure: f }) => {
      if (!alive) return;
      setCourse(c);
      setCourseLive(live);
      setFailure(f);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [settings.courseId, reloadNonce]);

  // Any screen can ask for the search sheet by firing this; the shell owns it.
  useEffect(() => {
    const open = () => setSheetOpen(true);
    window.addEventListener('fairway:open-search', open);
    return () => window.removeEventListener('fairway:open-search', open);
  }, []);

  // --- cart paths ---------------------------------------------------------
  useEffect(() => {
    let alive = true;
    setCartPaths(null);
    if (!course.id) return;
    loadCartPaths(course.id, course.centre).then((g) => alive && setCartPaths(g));
    return () => {
      alive = false;
    };
  }, [course.id, course.centre]);

  // --- gps ----------------------------------------------------------------
  const { fix, error: gpsError } = useGps({ simulate: settings.demoDrive ? simPos : null });

  // Demo drive: walk the buggy tee -> green so the whole stack can be shown
  // working on a desk, indoors, with no GPS.
  useEffect(() => {
    if (!settings.demoDrive) {
      setSimPos(null);
      return;
    }
    if (!hole) return;
    const tee = hole.tees[settings.teeColour] ?? hole.tees.White;
    const total = distanceM(tee, hole.green.middle);
    let travelled = 0;
    setSimPos(tee);
    const id = window.setInterval(() => {
      travelled = (travelled + 12) % (total + 40);
      const b = Math.atan2(
        hole.green.middle[0] - tee[0],
        hole.green.middle[1] - tee[1]
      );
      setSimPos(destination(tee, ((b * 180) / Math.PI + 360) % 360, Math.min(travelled, total)));
    }, 1500);
    return () => window.clearInterval(id);
  }, [settings.demoDrive, settings.teeColour, hole]);

  // --- weather ------------------------------------------------------------
  useEffect(() => {
    // Without a fix and without a course, "here" has no answer worth asking
    // the weather service about — [0,0] is in the Atlantic.
    const at = fix?.pos ?? (course.id ? course.centre : null);
    if (!at) return;
    void fetchWeather(at).then(setWeather);
    const id = window.setInterval(() => void fetchWeather(at).then(setWeather), 10 * 60_000);
    return () => window.clearInterval(id);
  }, [course.id, course.centre, fix?.pos]);

  // --- auto hole detection -----------------------------------------------
  // Sequence-aware detector: see lib/holeDetector.ts. Runs on every fix for the
  // whole round, which is the "4 hour Waze trip" behaviour — the golfer starts
  // it once and never touches the hole buttons unless they want to.
  useEffect(() => {
    // A pinned hole is absolute: the golfer has told us where they are and no
    // amount of GPS evidence overrides that until they unpin it.
    if (settings.lockHoleNumber) return;
    if (!fix || fix.stale || !course.holes.length) return;
    setDetector((d) =>
      detectStep(d, {
        course,
        pos: fix.pos,
        accuracy: fix.accuracy,
        teeColour: settings.teeColour,
        now: fix.ts,
      })
    );
  }, [fix, course, settings.teeColour, settings.lockHoleNumber]);

  // --- keep-out fence -----------------------------------------------------
  const breach = useMemo(() => {
    if (!fix || fix.stale) return null;
    const z = firstBreachedZone(fix.pos, course.keepOut);
    return z ? { label: z.label, severity: z.severity } : null;
  }, [fix, course.keepOut]);

  // --- synced hole flicking (phone app <-> CarPlay, both live during a round) --
  // Current hole lives in the detector; a flick on either surface broadcasts it
  // over the fleet socket and the other surface follows. Refs keep the socket
  // handler reading fresh state without re-opening the connection.
  const roundActive = !!round && !round.finishedAt;
  const roundActiveRef = useRef(roundActive);
  roundActiveRef.current = roundActive;
  const courseRef = useRef(course);
  courseRef.current = course;
  /** The hole a remote message just applied, so our own publish effect does not
   *  echo it straight back and start a ping-pong. */
  const lastAppliedRemoteHole = useRef<number | null>(null);

  // --- fleet socket -------------------------------------------------------
  const fleet = useFleet({
    courseId: course.id,
    role: settings.role,
    buggyId: settings.buggyId,
    onHole: (bid, holeNum) => {
      // Only my own buggy's other surface, only during a round, only real holes.
      if (bid !== settings.buggyId || !roundActiveRef.current) return;
      if (!courseRef.current.holes.some((h) => h.number === holeNum)) return;
      lastAppliedRemoteHole.current = holeNum;
      setDetector((d) => setManual(d, holeNum, Date.now()));
    },
  });
  const lastAlert = useRef<string | null>(null);

  // Broadcast our current hole whenever it changes during a live round — covers
  // a manual flick, a voice command, or auto-detection. Skips the one change
  // that a remote message just applied, so the two surfaces settle instead of
  // fighting.
  const publishHoleRef = useRef(fleet.publishHole);
  publishHoleRef.current = fleet.publishHole;
  const holeNumber = hole?.number ?? null;
  useEffect(() => {
    if (!roundActive || holeNumber == null) return;
    if (holeNumber === lastAppliedRemoteHole.current) {
      lastAppliedRemoteHole.current = null;
      return;
    }
    publishHoleRef.current(holeNumber);
  }, [holeNumber, roundActive]);

  useEffect(() => {
    if (settings.role !== 'buggy' || !fix || fix.stale || !hole) return;
    fleet.publish({
      buggyId: settings.buggyId,
      lng: fix.pos[0],
      lat: fix.pos[1],
      speed: fix.speed,
      heading: fix.heading,
      hole: hole.number,
      battery: null,
      alert: breach?.severity === 'block' ? 'keepout' : null,
    });
    const key = breach?.severity === 'block' ? breach.label : null;
    if (key && key !== lastAlert.current) {
      fleet.sendAlert('keepout', key);
      // Audible warning matters more than the banner — the driver is looking
      // out the front, not at the screen.
      beep();
    }
    lastAlert.current = key;
  }, [fix, breach, hole, settings.role, settings.buggyId, fleet]);

  const others = useMemo(
    () => Object.values(fleet.positions).filter((p) => p.buggyId !== settings.buggyId),
    [fleet.positions, settings.buggyId]
  );

  // --- outbox drain -------------------------------------------------------
  useEffect(() => {
    const tick = async () => {
      if (!navigator.onLine) return;
      await drainOutbox();
      setQueued(outboxSize());
    };
    void tick();
    const id = window.setInterval(tick, 20_000);
    window.addEventListener('online', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('online', tick);
    };
  }, []);

  // --- round persistence --------------------------------------------------
  useEffect(() => {
    if (round) localStorage.setItem(ROUND_KEY, JSON.stringify(round));
    else localStorage.removeItem(ROUND_KEY);
  }, [round]);

  const startRound = useCallback(
    (players: Player[], format: ScoreFormat) => {
      const r = newRound({ course, buggyId: settings.buggyId, players, format });
      setRound(r);
      void apiPostDurable('/api/rounds', r).then(() => setQueued(outboxSize()));
      setTab('map');
    },
    [course, settings.buggyId]
  );

  const endRound = useCallback(() => {
    if (!round) return;
    const done = { ...round, finishedAt: Date.now(), rev: round.rev + 1 };
    void apiPostDurable('/api/rounds', done).then(() => setQueued(outboxSize()));
    setRound(null);
    setTab('setup');
  }, [round]);

  const patchScore = useCallback(
    (playerId: string, holeNo: number, patch: Partial<HoleScore>) => {
      setRound((r) => {
        if (!r) return r;
        const card = (r.scores[playerId] ?? []).map((s) =>
          s.hole === holeNo ? { ...s, ...patch } : s
        );
        const next = { ...r, scores: { ...r.scores, [playerId]: card }, rev: r.rev + 1 };
        void apiPostDurable('/api/rounds', next).then(() => setQueued(outboxSize()));
        return next;
      });
    },
    []
  );

  /**
   * Act on a spoken command. Returns what to show the golfer, or null if it did
   * not parse — the mic UI turns that into the right sound.
   */
  const handleSpoken = useCallback(
    async (transcript: string): Promise<string | null> => {
      const dir = await loadDirectory().catch(() => null);
      const cmd = parseCommand(transcript, {
        courses: dir?.courses ?? [],
        near: fix ? { lat: fix.pos[1], lng: fix.pos[0] } : null,
      });

      switch (cmd.kind) {
        case 'course':
          update({ courseId: cmd.course.id, courseName: cmd.course.name });
          setPickedName(cmd.course.name);
          setCourse(provisionalCourse(cmd.course));
          setDetector(initialState(1));
          setTab('map');
          return `Starting ${cmd.course.name}`;
        case 'hole': {
          const i = course.holes.findIndex((h) => h.number === cmd.hole);
          if (i < 0) return null;
          setDetector((d) => setManual(d, cmd.hole, Date.now()));
          return `Hole ${cmd.hole}`;
        }
        case 'next':
          if (holeIndex >= course.holes.length - 1) return null;
          setDetector((d) => setManual(d, course.holes[holeIndex + 1].number, Date.now()));
          return `Hole ${course.holes[holeIndex + 1].number}`;
        case 'previous':
          if (holeIndex <= 0) return null;
          setDetector((d) => setManual(d, course.holes[holeIndex - 1].number, Date.now()));
          return `Hole ${course.holes[holeIndex - 1].number}`;
        case 'park':
          if (!fix) return null;
          park(fix.pos, hole?.number ?? null);
          return 'Buggy parked here';
        case 'unpark':
          clearParked();
          return 'Buggy marker cleared';
        case 'ambiguous':
          return `Did you mean ${cmd.options.map((o) => o.name).slice(0, 2).join(' or ')}?`;
        default:
          return null;
      }
    },
    [course, fix, hole, holeIndex, park, clearParked, update]
  );

  const goHole = useCallback(
    (i: number) => {
      if (i < 0 || i >= course.holes.length) return;
      // A manual pick beats the detector for a few minutes, then auto resumes.
      setDetector((d) => setManual(d, course.holes[i].number, Date.now()));
    },
    [course.holes]
  );


  const pickCourse = useCallback(
    (hit: SearchHit) => {
      // The name is persisted, not just held in state: on a relaunch the course
      // is loaded from a saved id, and if that load fails there is otherwise
      // nothing to call it — the error screen said "This course".
      update({ courseId: hit.id, courseName: hit.name });
      setPickedName(hit.name);
      setCourse(provisionalCourse(hit));
      setDetector(initialState(1));
      setTab('map');
    },
    [update]
  );

  return (
    <div className={`app ${settings.bigText ? 'big-text' : ''}`}>
      {/* Owned by the shell so it exists on every screen. Living inside the map
          meant that when a course failed to load there was no listener for the
          open-search event, and both buttons on the error screen did nothing. */}
      <CommandSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        at={fix?.pos ?? null}
        onPickCourse={pickCourse}
        actions={[
          {
            label: parked ? 'Clear parked buggy' : 'Park here',
            detail: parked ? 'forget where the buggy is' : 'mark where the buggy is',
            active: !!parked,
            onPick: parked ? clearParked : () => fix && park(fix.pos, hole?.number ?? null),
          },
          {
            label: settings.lockHoleNumber ? 'Unpin hole' : 'Pin this hole',
            detail: settings.lockHoleNumber
              ? 'let GPS pick the hole again'
              : 'stop GPS changing the hole',
            active: settings.lockHoleNumber,
            onPick: () => update({ lockHoleNumber: !settings.lockHoleNumber }),
          },
          ...(voiceSupported()
            ? [{
                label: settings.voice ? 'Turn voice off' : 'Turn voice on',
                detail: 'spoken distances when you stop',
                active: settings.voice,
                onPick: () => {
                  if (!settings.voice) unlockVoice();
                  update({ voice: !settings.voice });
                },
              }]
            : []),
        ]}
      />

      <header className="topbar">
        <button
          className="searchbtn"
          onClick={() => window.dispatchEvent(new Event('fairway:open-search'))}
          aria-label="Search courses"
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
            <path d="M15.4 15.4 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
        <span className="brand">{BRAND.name}</span>
        <span className="pill">{settings.buggyId}</span>
        <span className={`pill ${fleet.status === 'open' ? 'ok' : fleet.status === 'offline' ? 'bad' : 'warn'}`}>
          {fleet.status === 'open' ? 'LINKED' : fleet.status.toUpperCase()}
        </span>
        {fix && (
          <span className={`pill ${fix.stale ? 'bad' : fix.accuracy < 12 ? 'ok' : 'warn'}`}>
            GPS ±{Math.round(fix.accuracy)}m
          </span>
        )}
        {queued > 0 && <span className="pill warn">{queued} QUEUED</span>}
        {hole && detector.confidence < 0.35 && (
          <span className="pill warn">HOLE {hole.number}?</span>
        )}
      </header>

      {tab === 'map' && (hole || !chosen) && (
        <SwipePager
          index={panel}
          onIndex={setPanel}
          panels={[
            { key: 'map', node: (
        <MapScreen
          course={course}
          /* courseMode off = no hole = a plain positioned map. HoleMap already
             renders exactly that when hole is null, so the "driving there"
             view needs no second component — it IS this map, minus the golf. */
          hole={settings.courseMode ? hole : null}
          holeIndex={holeIndex}
          onHole={goHole}
          teeColour={settings.teeColour}
          unit={settings.unit}
          fix={fix}
          weather={weather}
          fleet={others}
          showFleet
          breach={breach}
          cartPaths={cartPaths}
          lockToHole={settings.lockToHole}
          voice={settings.voice}
          onSpokenCommand={handleSpoken}
          parked={parked}
          mapMode={settings.mapMode}
          confidence={detector.confidence}
          indicators={settings.indicators}
          cartPathMode={settings.cartPathMode}
        />
            ) },
            ...(FEATURES.scoring && round
              ? [{ key: 'score', node: (
                  <ScoreScreen
                    course={course}
                    round={round}
                    holeIndex={holeIndex}
                    onHole={goHole}
                    onScore={patchScore}
                    onFinish={endRound}
                  />
                ) }]
              : []),
            ...(hole ? [{ key: 'analytics', node: (
              <AnalyticsPanel
                course={course}
                hole={hole}
                holeIndex={holeIndex}
                teeColour={settings.teeColour}
                unit={settings.unit}
                fix={fix}
                weather={weather}
                parked={parked}
                confidence={detector.confidence}
                cartPathCount={cartPaths ? cartPaths.features.length : null}
              />
            ) }] : []),
          ]}
        />
      )}

      {tab === 'map' && chosen && !hole && (
        <div className="screen">
          <div className="card">
            {loading ? (
              <>
                <h2>BUILDING THE COURSE</h2>
                <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>{courseName}</div>
                <div className="muted">
                  First time anyone has opened this one. Reading its holes, greens and bunkers
                  from the map data — up to a minute. Every buggy after this gets it instantly.
                </div>
              </>
            ) : (
              <>
                <h2>{failure === 'unavailable' ? 'COULD NOT REACH THE MAP DATA' : 'HOLES NOT MAPPED'}</h2>
                <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>{courseName}</div>
                <div className="muted">
                  {failure === 'unavailable'
                    ? 'The map service was busy. Nothing is wrong with the course — try again in a moment.'
                    : `OpenStreetMap has this course but nobody has drawn its holes yet, and ${BRAND.name} will not invent yardages.`}
                </div>
                <div className="row" style={{ marginTop: 10 }}>
                  {failure === 'unavailable' && (
                    <button className="btn primary" onClick={() => setReloadNonce((n) => n + 1)}>
                      Try again
                    </button>
                  )}
                  <button
                    className="btn"
                    onClick={() => window.dispatchEvent(new Event('fairway:open-search'))}
                  >
                    Pick another course
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'courses' && (
        <CoursesScreen
          fix={fix}
          activeCourseId={course.id}
          onPick={pickCourse}
        />
      )}

      {tab === 'widget' && hole && (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%', padding: 24, gap: 16 }}>
          <div className="muted">
            Dashboard tile preview — this is what sits on the head unit home screen at{' '}
            <strong>/widget</strong>
          </div>
          <div style={{ width: 380, height: 240 }}>
            <WidgetScreen
              course={course}
              hole={hole}
              teeColour={settings.teeColour}
              unit={settings.unit}
              fix={fix}
              onOpen={() => setTab('map')}
              brand={BRAND.name}
            />
          </div>
          <div style={{ width: 240, height: 240 }}>
            <WidgetScreen
              course={course}
              hole={hole}
              teeColour={settings.teeColour}
              unit={settings.unit}
              fix={fix}
              onOpen={() => setTab('map')}
              brand={BRAND.name}
            />
          </div>
        </div>
      )}

      {tab === 'score' && round && FEATURES.scoring && (
        <ScoreScreen
          course={course}
          round={round}
          holeIndex={holeIndex}
          onHole={goHole}
          onScore={patchScore}
          onFinish={endRound}
        />
      )}

      {tab === 'fleet' && FEATURES.fleet && (
        <FleetScreen course={course} positions={fleet.positions} status={fleet.status} />
      )}

      {tab === 'post' && round && FEATURES.federation && (
        <PostScoreScreen
          course={course}
          round={round}
          regionCode={settings.regionCode}
          onRegion={(code) => update({ regionCode: code, unit: getRegion(code).unit })}
        />
      )}

      {tab === 'survey' && FEATURES.survey && <SurveyScreen course={course} fix={fix} unit={settings.unit} />}

      {/* Score tab with no round yet: MiScore lets you start scoring from here,
          so a golfer who taps Score before setting up isn't met with a blank. */}
      {tab === 'score' && !round && FEATURES.scoring && (
        <div className="screen">
          <div className="card">
            <h2>NO ROUND YET</h2>
            <div className="muted" style={{ marginBottom: 10 }}>
              Start a round to keep score — Stableford, stroke or match.
            </div>
            <button className="btn primary" onClick={() => setTab('setup')}>
              Start a round
            </button>
          </div>
        </div>
      )}

      {tab === 'setup' && (
        <SetupScreen
          course={course}
          courseLive={courseLive}
          settings={settings}
          onSettings={update}
          round={round}
          onStartRound={startRound}
          onEndRound={endRound}
          gpsError={gpsError}
        />
      )}

      {/* Persistent MiScore-style bottom nav — buggy/golfer role only; the shop
          dashboard has its own layout. */}
      {settings.role === 'buggy' && (
        <BottomNav active={tab} onSelect={(t) => setTab(t as Tab)} />
      )}
    </div>
  );
}

let audioCtx: AudioContext | null = null;
function beep() {
  try {
    audioCtx ??= new AudioContext();
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.frequency.value = 880;
    g.gain.value = 0.15;
    o.connect(g).connect(audioCtx.destination);
    o.start();
    o.stop(audioCtx.currentTime + 0.25);
  } catch {
    /* head unit may block audio until first touch */
  }
}
